import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SelectItemProps } from '@ui/src';
import { AccountType, Category, ReportSheet } from '../../types';
import { capitalAdequacyApi } from './service';
import { CapitalAdequacy } from '../types';
import formReport from '../../helpers/form-report';

export interface CapitalAdequacyState {
  report?: ReportSheet[];
  itemsVisible: AccountType[];
  formationTime?: {
    start?: number;
    end?: number;
    duration?: number;
  };
  lists: {
    categories: SelectItemProps[];
    groups: SelectItemProps[];
    primaries: SelectItemProps[];
  };
  ongoingCapitalRequirement?: number;
  excessEligibleEquity?: number;
  minimalCapitalRequirement?: number;
  applicableOngoingCapitalRequirement?: number;
  eligibleEquity?: number;
  averageOutstandingEmoney?: number;
  accountsCategories?: Category[];
}

const initialState: CapitalAdequacyState = {
  report: undefined,
  itemsVisible: [],
  formationTime: undefined,
  lists: {
    categories: [],
    groups: [],
    primaries: [],
  },
};

export const capitalAdequacySlice = createSlice({
  name: 'capitalAdequacy',
  initialState,
  reducers: {
    changeVisibility: (state, action: PayloadAction<AccountType[]>) => {
      state.itemsVisible = action.payload;
    },
    setCAAccountsCategories: (state, action: PayloadAction<Category[]>) => {
      state.accountsCategories = action.payload;
    },
    clearCapitalAdequacyState: (state) => {
      state.report = undefined;
      state.itemsVisible = [];
      state.formationTime = undefined;
      state.ongoingCapitalRequirement = 0;
      state.excessEligibleEquity = 0;
      state.minimalCapitalRequirement = 0;
      state.applicableOngoingCapitalRequirement = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        capitalAdequacyApi.endpoints.getCapitalAdequacyReport.matchPending,
        (state) => {
          state.formationTime = {
            start: Date.now(),
            end: undefined,
            duration: undefined,
          };
        },
      )
      .addMatcher(
        capitalAdequacyApi.endpoints.getCapitalAdequacyReport.matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: CapitalAdequacy }> & {
            meta: { fulfilledTimeStamp: number };
          },
        ) => {
          const {
            ongoingCapitalRequirement,
            excessEligibleEquity,
            minimalCapitalRequirement,
            applicableOngoingCapitalRequirement,
            eligibleEquity,
            averageOutstandingEmoney,
          } = action.payload.data;

          const accountCategories = state.accountsCategories;

          const reportResult = formReport({
            ...action.payload.data,
            accountCategories: accountCategories as Category[],
          });

          state.lists = reportResult.lists;
          state.report = reportResult.sheet;

          state.ongoingCapitalRequirement = ongoingCapitalRequirement || 0;
          state.excessEligibleEquity = excessEligibleEquity || 0;
          state.minimalCapitalRequirement = minimalCapitalRequirement || 0;
          state.applicableOngoingCapitalRequirement =
            applicableOngoingCapitalRequirement || 0;

          state.eligibleEquity = eligibleEquity || 0;
          state.averageOutstandingEmoney = averageOutstandingEmoney || 0;

          state.formationTime = {
            ...state.formationTime,
            end: action.meta.fulfilledTimeStamp,
            duration: Math.round(
              (action.meta.fulfilledTimeStamp -
                (state.formationTime?.start as number)) /
                1000,
            ),
          };
        },
      );
  },
});

export const {
  clearCapitalAdequacyState,
  changeVisibility,
  setCAAccountsCategories,
} = capitalAdequacySlice.actions;

export default capitalAdequacySlice.reducer;
